import React, { useState, useEffect, useRef } from 'react';
import { Calendar, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DEMO_CALENDAR_URL } from '@/constants';

interface ATSCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
}

const DEMO_ROUTE = '/agendar-demo';

const ATSCalendarModal: React.FC<ATSCalendarModalProps> = ({ 
  isOpen, 
  onClose,
  title = "Reserve su demo de Nexus ATS",
  subtitle = "Elija el horario que mejor le convenga"
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const previousPathRef = useRef<string | null>(null);

  // Sync URL with modal state (mirrors ContactFormModal behavior)
  useEffect(() => {
    if (isOpen) {
      const current = window.location.pathname + window.location.search + window.location.hash;
      if (window.location.pathname !== DEMO_ROUTE) {
        previousPathRef.current = current;
        window.history.pushState({ demoModal: true }, '', DEMO_ROUTE);
      }
    }
  }, [isOpen]);

  // Close modal when user navigates back
  useEffect(() => {
    const handlePopState = () => {
      if (isOpen) onClose();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOpen, onClose]);

  const handleClose = () => {
    if (previousPathRef.current && window.location.pathname === DEMO_ROUTE) {
      window.history.back();
      previousPathRef.current = null;
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-4xl w-[95vw] h-[85vh] p-0 overflow-hidden border-0 bg-gradient-to-br from-background via-background to-primary/5 shadow-2xl">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-primary to-secondary p-6 text-primary-foreground">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Cerrar modal de agendamiento"
            className="absolute right-4 top-4 text-primary-foreground/80 hover:text-primary-foreground hover:bg-white/10 min-h-11 min-w-11"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg">
              <Calendar className="h-6 w-6" />
            </div>
            <div>
              <h2 className="font-exo text-xl md:text-2xl font-bold">
                {title}
              </h2>
              <p className="text-sm opacity-80 mt-1">
                {subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Calendar iframe container */}
        <div className="flex-1 w-full h-full relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Cargando calendario...</p>
              </div>
            </div>
          )}
          <iframe
            src={DEMO_CALENDAR_URL}
            className="w-full h-full border-0"
            style={{ minHeight: 'calc(85vh - 100px)' }}
            title="Agendar demo de Nexus ATS"
            onLoad={() => setIsLoading(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ATSCalendarModal;
